import React, { useState, useMemo, useEffect } from "react";
import { useBucketCollections } from "../hooks/useBucketCollections";
import { SubscriptionClient } from "../sdk";
import { formatCurrency, formatPercentage } from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  WarningMessage,
  LayoutHeader,
  TableHeaderCell,
} from "./smartlayout/fragments";
import {
  DEFAULT_BASE_URL,
  DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  DEFAULT_REFRESH_INTERVAL,
} from "../constants";
import "./smartlayout/layout.css";
import { Bucket, calculateRiskRatio, getDisplayMarginType } from "../sdk";

interface BucketCollectionsProps {
  baseURL: string;
  /** Optional: when inside SubscriptionProvider, hook uses context; pass for backward compat or when outside provider. */
  subscriptionClient?: SubscriptionClient;
  walletAddress: string;
  mnemonic: string; // Required for transfer operations
  authToken?: string;
  height?: string;
  refreshInterval?: number;
  onBucketSelect?: (bucketId: string, totalEquity: number) => void;
  /** When true, do not render LayoutHeader (e.g. when used inside TabSection). */
  hide_header?: boolean;
  /** When hide_header is true, call with the header actions node so parent can show them in a shared toolbar. */
  onRequestHeaderActions?: (node: React.ReactNode) => void;
  /** Called when Transfer button is clicked; opens transfer modal at page level when provided. */
  onRequestOpenTransferModal?: () => void;
  /** Called when Send to Address button is clicked; opens wallet transfer modal when provided. */
  onRequestOpenWalletTransferModal?: () => void;
}

type SortField =
  | "marginType"
  | "usedMargin"
  | "totalEquity"
  | "riskRatio"
  | "bucketId";
type SortDirection = "asc" | "desc";

function BucketCollections({
  baseURL = DEFAULT_BASE_URL,
  subscriptionClient,
  authToken,
  walletAddress,
  mnemonic: _mnemonic,
  height = DEFAULT_HEIGHT_BUCKET_COLLECTIONS,
  refreshInterval = DEFAULT_REFRESH_INTERVAL,
  onBucketSelect,
  hide_header = false,
  onRequestHeaderActions,
  onRequestOpenTransferModal,
  onRequestOpenWalletTransferModal,
}: BucketCollectionsProps): JSX.Element {
  const {
    collections,
    loading,
    error,
    isConnected,
    refreshBucketBalance,
  } = useBucketCollections({
    ...(subscriptionClient != null && { subscriptionClient }),
    baseURL,
    authToken,
    walletAddress,
    refreshInterval,
  });

  // Removed debug logging for better performance

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedBucketId, setSelectedBucketId] = useState<string>("");
  const [filterMode, setFilterMode] = useState<
    "all" | "non-zero" | "dormant" | "active"
  >("non-zero");
  // Sort and filter collections
  const sortedCollections = useMemo(() => {
    // Collections are already filtered in the hook, but double-check for safety
    let validCollections = collections.filter(
      (c) =>
        c &&
        c.bucketId &&
        typeof c.bucketId === "string" &&
        c.bucketId.trim() !== "",
    );

    // Apply filters based on filterMode
    if (filterMode === "non-zero") {
      validCollections = validCollections.filter(
        (c) => (c.totalEquity || 0) > 0,
      );
    } else if (filterMode === "dormant") {
      validCollections = validCollections.filter(
        (c) => c.status?.toLowerCase() === "dormant",
      );
    } else if (filterMode === "active") {
      validCollections = validCollections.filter(
        (c) => c.status?.toLowerCase() === "active",
      );
    }
    // "all" mode shows all valid collections without additional filtering

    if (!sortField) {
      return validCollections;
    }

    return [...validCollections].sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sortField) {
        case "marginType":
          aValue = getDisplayMarginType(a) || "";
          bValue = getDisplayMarginType(b) || "";
          break;
        case "usedMargin":
          aValue = a.usedMargin || 0;
          bValue = b.usedMargin || 0;
          break;
        case "totalEquity":
          aValue = a.totalEquity || 0;
          bValue = b.totalEquity || 0;
          break;
        case "riskRatio":
          aValue = calculateRiskRatio(a);
          bValue = calculateRiskRatio(b);
          break;
        case "bucketId":
          aValue = a.bucketId || "";
          bValue = b.bucketId || "";
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === "asc" ? numA - numB : numB - numA;
    });
  }, [collections, sortField, sortDirection, filterMode]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleRowClick = (bucketId: string, available_margin: number) => {
    if (onBucketSelect) {
      onBucketSelect(bucketId, available_margin);
    }
    setSelectedBucketId(bucketId);
  };

  const handleFilterToggle = () => {
    setFilterMode((prev) => {
      // Cycle through: non-zero -> active -> dormant -> all -> non-zero
      switch (prev) {
        case "non-zero":
          return "active";
        case "active":
          return "dormant";
        case "dormant":
          return "all";
        case "all":
          return "non-zero";
        default:
          return "non-zero";
      }
    });
  };

  const headerActions = useMemo(
    () => (
      <>
        <div
          onClick={handleFilterToggle}
          className="btn-tab"
          title={
            filterMode === "non-zero"
              ? "Click to show active buckets"
              : filterMode === "active"
                ? "Click to show dormant buckets"
                : filterMode === "dormant"
                  ? "Click to show all buckets"
                  : "Click to show non-zero balance buckets"
          }
        >
          <span>
            {filterMode === "all"
              ? "All"
              : filterMode === "non-zero"
                ? "Non-Zero"
                : filterMode === "dormant"
                  ? "Dormant"
                  : "Active"}
          </span>
        </div>
        <div
          onClick={() => onRequestOpenTransferModal?.()}
          className="btn-tab"
          style={{ cursor: onRequestOpenTransferModal ? "pointer" : undefined }}
        >
          <img src="/transfer-repu.svg" />
          <span>Transfer</span>
        </div>
        {onRequestOpenWalletTransferModal && (
          <div
            onClick={() => onRequestOpenWalletTransferModal()}
            className="btn-tab"
            style={{ cursor: "pointer" }}
          >
            <span>Send to Address</span>
          </div>
        )}
        {!isConnected && (
          <div
            onClick={async () => {
              if (refreshBucketBalance) {
                await refreshBucketBalance();
              }
            }}
            className="btn-tab"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title="WebSocket disconnected - Click to refresh bucket collections"
          >
            <img
              src="/caution-icy.svg"
              alt="Warning"
              style={{ width: "1rem", height: "1rem" }}
            />
            <span>Refresh</span>
          </div>
        )}
      </>
    ),
    [
      filterMode,
      handleFilterToggle,
      onRequestOpenTransferModal,
      onRequestOpenWalletTransferModal,
      isConnected,
      refreshBucketBalance,
    ],
  );

  useEffect(() => {
    if (!onRequestHeaderActions) return;
    if (hide_header && !loading && !error) {
      onRequestHeaderActions(headerActions);
      return () => onRequestHeaderActions(null);
    }
    onRequestHeaderActions(null);
  }, [hide_header, onRequestHeaderActions, loading, error, headerActions]);

  // Show appropriate message when wallet address is not available
  if (!walletAddress || walletAddress.trim() === "") {
    return (
      <WarningMessage message="Wallet address required to view bucket collections" />
    );
  }

  // Only show loading message on initial load when there's no data yet
  if (loading && collections.length === 0) {
    return <LoadingMessage message="Loading bucket collections..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div
      className="layout-container"
      style={{
        height,
        ...(hide_header
          ? { borderRadius: 0, background: "transparent" }
          : {}),
      }}
    >
      {!hide_header && (
        <LayoutHeader title="BUCKET COLLECTIONS">{headerActions}</LayoutHeader>
      )}

      {/* Table */}
      <div className="table-container shrinkable">
        {/* Table Header */}
        <div
          className="table-header"
          style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
        >
          <TableHeaderCell
            field="bucketId"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            left
          >
            Bucket ID
          </TableHeaderCell>
          <TableHeaderCell
            field="marginType"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Margin Type
          </TableHeaderCell>
          <TableHeaderCell
            field="usedMargin"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Used Margin
          </TableHeaderCell>
          <TableHeaderCell
            field="totalEquity"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Total Equity
          </TableHeaderCell>
          <TableHeaderCell
            field="riskRatio"
            currentSortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            left={false}
          >
            Risk Ratio
          </TableHeaderCell>
        </div>
        {/* Table Body */}
        {sortedCollections.length === 0 ? (
          <EmptyMessage
            message={
              collections.length === 0
                ? "No bucket collections found. Make sure your wallet address is correct and has buckets."
                : `Filtered out all ${collections.length} collections (invalid data structure)`
            }
          />
        ) : (
          sortedCollections.map((collection: Bucket, index: number) => {
            const riskRatio = calculateRiskRatio(collection);
            const displayMarginType = getDisplayMarginType(collection);
            return (
              <div
                key={`collection-${collection.bucketId}-${index}`}
                onClick={() =>
                  handleRowClick(
                    collection.bucketId,
                    collection.availableMargin,
                  )
                }
                className={`table-row clickable ${index % 2 === 0 ? "even" : ""} ${selectedBucketId === collection.bucketId ? "selected" : ""}`}
                style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}
              >
                <div className="table-cell primary">
                  {collection.bucketId || "---"}
                </div>
                <div className="table-cell">
                  {displayMarginType.toUpperCase()}
                </div>
                <div className="table-cell right monospace">
                  {formatCurrency(collection.usedMargin || 0)}
                </div>
                <div className="table-cell right monospace">
                  {formatCurrency(collection.totalEquity || 0)}
                </div>
                <div
                  className="table-cell right monospace"
                  style={{
                    color: riskRatio > 0.5 ? "#ff6b6b" : "#51cf66",
                    fontWeight: 500,
                  }}
                >
                  {formatPercentage(riskRatio)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default React.memo(BucketCollections);
